import { Component, computed, inject } from '@angular/core';
import { PlayerService } from '../../data-access/player.service';
import { PlayerItemSource } from '../../data-access/player.config';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { DetailsService } from '@features/details/data-access/details.service';
import { CommonModule } from '@angular/common';
import { PlayerChannelsComponent } from './components/player-channels/player-channels.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { LiveStore } from '@features/live';

@Component({
  selector: 'player-header',
  imports: [
    CommonModule,
    RoundedIconButton,
    PlayerChannelsComponent
  ],
  templateUrl: './player-header.component.html',
  styleUrl: './player-header.component.scss'
})
export class PlayerHeaderComponent {
  public skinning = inject(SkinningService);
  private detailsService = inject(DetailsService);
  private playerService = inject(PlayerService);
  private liveStore = inject(LiveStore);
  private breakpointObserver = inject(BreakpointObserver);
  public isPip = this.playerService.isPlayerPip();
  private item = this.playerService.getItem();
  // Same breakpoints as PlayerControlsComponent.isCompactLayout - the two templates swap together.
  public isCompactLayout = toSignal(this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait]));
  public isAdvertisingRunning = this.playerService.isAdvertisingRunning;
  public isCurrentItemLive = this.playerService.isCurrentItemLive;
  public isCastAvailable = this.playerService.isCastAvailable();
  public channelIcon = computed(() => {
    const channelId = this.item()?.channelReferenceId;
    if (!channelId) return null;
    return this.liveStore.channels().find(c => c.referenceId === channelId)?.icon ?? null;
  })
  public itemSeriesTitle = computed(() => {
    return this.item()?.seriesTitle;
  })
  // While live, the title is the programme on air (what the controls' title shows too), not the channel.
  public itemTitle = computed(() => {
    const channelId = this.item()?.channelReferenceId;
    if (this.isCurrentItemLive() && channelId) {
      return this.liveStore.liveOf(channelId)?.title || this.item()?.title;
    }
    return this.item()?.title;
  })
  public isChannel = computed(() => {
    return [PlayerItemSource.TLIVE, PlayerItemSource.THEPG].includes(this.item()?.ng_source);
  })

  public openDetails() {
    const item = this.item();
    // The modal renders outside the player element, so element-fullscreen would hide it entirely
    // (fullscreen is the browser's top layer — the overPlayer stacking below cannot beat it).
    // Drop fullscreen first; playback continues in the normal layout under the modal.
    this.playerService.exitFullscreen();
    // Opened from the player chrome — stack the modal above the active player. A live item played
    // through /channels/{id}/play is the CHANNEL (referenceId = channel id): open its current event.
    this.detailsService.open({
      item: { ...item, contentType: item.ng_contentType || item.ng_source } as any,
      overPlayer: true,
      channelCurrentEvent: true,
    });
  }

  public close() {
    this.playerService.close();
  }

  public togglePip() {
    this.playerService.togglePip();
  }

  public toggleCast() {
    this.playerService.toggleCast();
  }

}
